import { z } from 'zod';
import { isString } from '../shared/runtime-values.js';
import { buildRouteDirections, type OsrmStep, type RouteStep } from './route-directions.js';
import { fetchOsrm, isRouteCoordinate, MAX_ROUTE_SNAP_DISTANCE_M, OSRM_ENDPOINT, routeWaypointsAreNearby } from './osrm-request.js';
import { haversineKm } from './distance-math.js';
import { getCurrentLocale, t } from './i18n.js';

export type RouteMode = 'driving' | 'walking' | 'cycling';

export interface RouteOption {
  distanceKm: number;
  legDistancesKm: number[];
  durationSec: number;
  viaLabel: string;
  hasFerry: boolean;
  coords: Array<[number, number]>;
  steps: RouteStep[];
}

export interface RoutePlanResult {
  primary: RouteOption;
  alternatives: RouteOption[];
}

export type RoutePlanFailureReason = 'http' | 'network' | 'no_route' | 'invalid_response';

const ROUTE_PROVIDER_ERROR_CODES = ['NoSegment', 'NoRoute', 'InvalidUrl', 'InvalidService', 'InvalidVersion', 'InvalidOptions', 'InvalidQuery', 'InvalidValue', 'TooBig'] as const;
const routeProviderErrorSchema = z.object({ code: z.enum(ROUTE_PROVIDER_ERROR_CODES), message: z.string().optional() });
type RouteProviderErrorCode = typeof ROUTE_PROVIDER_ERROR_CODES[number];

export class RoutePlanError extends Error {
  constructor(
    public readonly reason: RoutePlanFailureReason,
    public readonly httpStatus?: number,
    public readonly providerCode?: RouteProviderErrorCode,
    public readonly stopIndex?: number,
    public readonly diagnostic?: 'provider_response' | 'empty_routes' | 'waypoint_mismatch' | 'invalid_geometry' | 'invalid_json',
  ) {
    super(`Route plan failed: ${reason}`);
    this.name = 'RoutePlanError';
  }

  get temporary(): boolean {
    return this.reason === 'network' || this.reason === 'invalid_response'
      || (this.reason === 'http' && (this.httpStatus === 429 || (this.httpStatus ?? 0) >= 500));
  }
}

type OsrmRouteStep = Partial<OsrmStep> & { geometry?: { coordinates: Array<[number, number]> } };
type OsrmLeg = { distance: number; duration: number; summary?: string; steps?: OsrmRouteStep[] };
type OsrmRoute = { distance: number; duration: number; legs: OsrmLeg[]; geometry?: { coordinates: Array<[number, number]> } };
type OsrmResponse = { code: string; waypoints?: Array<{ location: [number, number] }>; routes: OsrmRoute[] };

export interface DetailedRouteOption extends RouteOption {
  legCoords: Array<Array<[number, number]>>;
  legGapCoords: Array<Array<[number, number]> | null>;
  allLegsRouted: boolean;
}

function parseRoutePlan(data: OsrmResponse, waypoints: Array<{ lat: number; lng: number }>, httpStatus: number, overview: 'full' | 'false'): DetailedRouteOption[] {
  if (data?.code !== 'Ok') {
    const parsed = routeProviderErrorSchema.safeParse(data);
    throw new RoutePlanError('no_route', httpStatus, parsed.success ? parsed.data.code : undefined, undefined, 'provider_response');
  }
  if (!Array.isArray(data.routes) || !data.routes.length) throw new RoutePlanError('no_route', httpStatus, undefined, undefined, 'empty_routes');
  if (!routeWaypointsAreNearby(data.waypoints, waypoints)) throw new RoutePlanError('no_route', httpStatus, undefined, undefined, 'waypoint_mismatch');
  const invalid = () => new RoutePlanError('invalid_response', httpStatus, undefined, undefined, 'invalid_geometry');
  return data.routes.map(route => {
    if (!route || !Number.isFinite(route.distance) || route.distance < 0 || !Number.isFinite(route.duration) || route.duration < 0
      || !Array.isArray(route.legs) || route.legs.length !== waypoints.length - 1
      || !route.legs.every(leg => leg && Number.isFinite(leg.distance) && leg.distance >= 0 && Number.isFinite(leg.duration) && leg.duration >= 0
        && (leg.steps === undefined || Array.isArray(leg.steps)) && (leg.summary === undefined || isString(leg.summary)))) throw invalid();
    const legCoords = route.legs.map(leg => (leg.steps ?? []).flatMap(step => step?.geometry?.coordinates ?? []));
    const coords = overview === 'full' ? route.geometry?.coordinates : legCoords.flat();
    if (!Array.isArray(coords) || coords.length < 2 || !coords.every(isRouteCoordinate)
      || (overview === 'false' && legCoords.some(leg => leg.length < 2 || !leg.every(isRouteCoordinate)))) throw invalid();
    const directionLegs = route.legs.map(leg => ({ steps: (leg.steps ?? []).filter((step): step is OsrmStep =>
      Boolean(step?.maneuver?.type) && Number.isFinite(step.distance) && Number.isFinite(step.duration) && isString(step.name)) }));
    const steps = buildRouteDirections(directionLegs);
    const summary = route.legs.map(leg => leg.summary).find(Boolean)?.split(',')[0].trim();
    return {
      distanceKm: route.distance / 1000, durationSec: route.duration,
      legDistancesKm: route.legs.map(leg => leg.distance / 1000), coords, steps,
      viaLabel: summary ? t('route_directions.via').replace('{name}', summary) : '',
      hasFerry: steps.some(step => step.travelMode === 'ferry'), legCoords, allLegsRouted: true,
      legGapCoords: waypoints.slice(1).map((point, index): Array<[number, number]> | null => {
        const snapped = data.waypoints![index + 1].location;
        return haversineKm(snapped[1], snapped[0], point.lat, point.lng) * 1000 > 500 ? [snapped, [point.lng, point.lat]] : null;
      }),
    };
  }).slice(0, 3);
}

export async function fetchRoutePlan(waypoints: Array<{ lat: number; lng: number }>, mode: RouteMode, signal?: AbortSignal): Promise<RoutePlanResult | null> {
  if (waypoints.length < 2) return null;
  const options = await fetchRouteOptions(waypoints, mode, signal);
  const [primary, ...alternatives] = options.map(({ legCoords: _legs, legGapCoords: _gaps, allLegsRouted: _routed, ...route }) => route);
  return { primary, alternatives };
}

export async function fetchRouteOptions(
  waypoints: Array<{ lat: number; lng: number }>,
  mode: RouteMode,
  signal?: AbortSignal,
  overview: 'full' | 'false' = 'full',
): Promise<DetailedRouteOption[]> {
  if (waypoints.length < 2) throw new RoutePlanError('no_route');
  const endpoint = OSRM_ENDPOINT[mode] ?? OSRM_ENDPOINT.driving;
  const coordStr = waypoints.map((w) => `${w.lng},${w.lat}`).join(';');
  const wantAlts = waypoints.length === 2;
  const radiuses = waypoints.map(() => MAX_ROUTE_SNAP_DISTANCE_M).join(';');
  const url = `${endpoint}/${coordStr}?overview=${overview}&geometries=geojson&steps=true&radiuses=${radiuses}${wantAlts ? '&alternatives=true' : ''}`;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let res: Response;
    try {
      res = await fetchOsrm(url, signal);
    } catch (error) {
      if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw error;
      if (attempt === 0) continue;
      throw new RoutePlanError('network');
    }

    if (!res.ok) {
      if (attempt === 0 && (res.status === 429 || res.status >= 500)) continue;
      let providerCode: RouteProviderErrorCode | undefined;
      let stopIndex: number | undefined;
      if (res.status === 400) {
        const parsed = routeProviderErrorSchema.safeParse(await res.json().catch(() => null));
        if (parsed.success) {
          providerCode = parsed.data.code;
          const match = parsed.data.message?.match(/^Could not find a matching segment for coordinate (\d+)$/);
          const index = match ? Number(match[1]) : -1;
          if (providerCode === 'NoSegment' && index >= 0 && index < waypoints.length) stopIndex = index;
        }
      }
      const reason = providerCode === 'NoSegment' || providerCode === 'NoRoute' ? 'no_route' : 'http';
      throw new RoutePlanError(reason, res.status, providerCode, stopIndex, 'provider_response');
    }

    let data: OsrmResponse;
    try {
      // SAFETY: The following parser checks every field that it accepts from this external payload.
      data = await res.json() as OsrmResponse;
    } catch {
      throw new RoutePlanError('invalid_response', res.status, undefined, undefined, 'invalid_json');
    }
    return parseRoutePlan(data, waypoints, res.status, overview);
  }
  throw new RoutePlanError('network');
}

function formatUnit(value: number, unit: string, fractionDigits = 0): string {
  return new Intl.NumberFormat(getCurrentLocale(), {
    style: 'unit', unit, unitDisplay: 'short',
    minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatDurationSec(sec: number): string {
  const minutes = Math.round(sec / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (getCurrentLocale() === 'en') {
    if (h === 0) return `${m} min`;
    return `${h} h ${m > 0 ? `${m} min` : ''}`.trim();
  }
  if (h === 0) return formatUnit(m, 'minute');
  return [formatUnit(h, 'hour'), ...(m ? [formatUnit(m, 'minute')] : [])].join(' ');
}

export function formatDistanceKm(km: number, useMetric: boolean): string {
  if (useMetric) {
    return km < 1 ? formatUnit(Math.round(km * 1000), 'meter') : formatUnit(km, 'kilometer', 1);
  }
  const mi = km * 0.621371;
  return mi < 0.1 ? formatUnit(Math.round(mi * 5280), 'foot') : formatUnit(mi, 'mile', 1);
}
